import { useEffect, useState, useMemo } from 'react';
import { useDataContext } from '../context/DataContext';
import { ENDPOINTS } from '../services/apiService';
import { transformData } from './utils';
import { useLoadingStore } from '../store/loadingStore';

const processEmissionDistributionData = (data: void | any[]) => {
  if (!data) return;
  const updated = data
    .map((item: any) => {
      if (item.type === 'Flare') {
        item.type = 'Flare / Combustor';
      }
      return item;
    })
    .filter((item) => item.type !== 'VRU')
    .filter((item) => item.type !== 'Generator')
    .sort((a, b) => {
      const order = [
        'Well',
        'Separator',
        'Tank',
        'Compressor',
        'Flare / Combustor',
        'Dehydrator',
        'Facility Piping',
      ];

      const orderA = order.indexOf(a.type);
      const orderB = order.indexOf(b.type);

      if (orderA > orderB) {
        return 1;
      } else {
        return -1;
      }
    });

  return updated;
};

export const useEquipmentLevel = ({
  period,
  time,
}: {
  period: string;
  time: string;
}) => {
  const yearsArray = useMemo(() => {
    if (period.includes('All')) {
      return ['2024'];
    } else {
      const periodParts = period.split(' ');
      return [periodParts[1]];
    }
  }, [period]);

  const quartersArray = useMemo(() => {
    if (period.includes('All')) {
      return ['1', '2', '3', '4'];
    } else {
      const periodParts = period.split(' ');
      return [periodParts[0].slice(1)];
    }
  }, [period]);

  const [allResults, setAllResults] = useState<any[]>([]);
  const [leakerResults, setLeakerResults] = useState<any[]>([]);
  const [totalResults, setTotalResults] = useState<any[]>([]);
  const { fetchMultipleAndSetData } = useDataContext();
  const setLoading = useLoadingStore(
    (state: { setLoading: any }) => state.setLoading,
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const results = await fetchMultipleAndSetData([
          {
            type: ENDPOINTS.calc_ar,
            params: {
              quarter: quartersArray.join(','),
              year: yearsArray.join(','),
            },
          },
          {
            type: ENDPOINTS.equipment_summary,
            params: {
              quarter: quartersArray.join(','),
              year: yearsArray.join(','),
            },
          },
        ]);

        if (results) {
          const calcArData = results.find(
            (r) => r.type === ENDPOINTS.calc_ar + '#' + 0,
          )?.data;
          const equipmentSummaryData = results.find(
            (r) => r.type === ENDPOINTS.equipment_summary + '#' + 1,
          )?.data;

          const transformedData = transformData(calcArData);
          /* Update data for Emission distribution charts */
          const updatedTransformedData =
            processEmissionDistributionData(transformedData);
          setAllResults(updatedTransformedData || []);

          setLeakerResults(equipmentSummaryData || []);
          setTotalResults(equipmentSummaryData || []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setLoading(false);
      } finally {
        setTimeout(() => setLoading(false), 7000);
      }
    };

    fetchData();
  }, [
    fetchMultipleAndSetData,
    period,
    time,
    quartersArray,
    yearsArray,
    setLoading,
  ]);

  return { allResults, totalResults, leakerResults };
};
